import { useState } from 'react';
import { submitContact } from '../api';

const STEPS = [
  {
    title: "What type of business are you?",
    field: 'project_type',
    options: ['SaaS', 'E-commerce', 'Marketplace', 'Services', 'Other'],
  },
  {
    title: "What's your current annual revenue?",
    field: 'revenue_range',
    options: ['Pre-revenue', '$0 - $100k', '$100k - $1M', '$1M+'],
  },
  {
    title: "What's your primary goal?",
    field: 'budget_range',
    options: ['New build from scratch', 'Rebuild / redesign existing product', 'Scale & optimize current product'],
  },
  {
    title: "What's your timeline?",
    field: 'timeline',
    options: ['ASAP', '1-3 months', '3-6 months', 'Just exploring'],
  },
];

export default function GrowthAuditPage() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ project_type: '', revenue_range: '', budget_range: '', timeline: '', name: '', email: '', phone: '', company: '', message: '' });
  const [status, setStatus] = useState(null);

  const handleOption = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    setTimeout(() => setStep(s => s + 1), 200);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await submitContact({ ...form, source_page: 'growth-audit' });
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  const totalSteps = STEPS.length + 1;
  const progress = ((step) / totalSteps) * 100;

  if (status === 'success') {
    return (
      <div className="pt-28 pb-20 min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-lg mx-auto px-4">
          <div className="text-6xl mb-6">&#10003;</div>
          <h1 className="text-3xl font-bold mb-4">Thank You!</h1>
          <p className="text-xl text-gray-600">We've received your growth audit request. Our team will review your information and get back to you within 24 hours.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-28 pb-20 min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-2xl">
        {/* Progress bar */}
        <div className="mb-12">
          <div className="flex justify-between text-sm text-gray-500 mb-2">
            <span>Step {step + 1} of {totalSteps}</span>
            <span>{Math.round(progress)}% complete</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-blue-600 h-2 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {step < STEPS.length ? (
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-8">{STEPS[step].title}</h1>
            <div className="space-y-3">
              {STEPS[step].options.map(opt => (
                <button key={opt} onClick={() => handleOption(STEPS[step].field, opt)}
                  className={`w-full text-left p-5 rounded-xl border-2 transition font-medium text-lg ${form[STEPS[step].field] === opt ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-blue-300 bg-white'}`}>
                  {opt}
                </button>
              ))}
            </div>
            {step > 0 && (
              <button onClick={() => setStep(s => s - 1)} className="mt-6 text-gray-500 hover:text-gray-700 text-sm">
                &larr; Back
              </button>
            )}
          </div>
        ) : (
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Almost done!</h1>
            <p className="text-gray-600 mb-8">Tell us how to reach you and we'll schedule your free growth audit.</p>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input type="text" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-600" placeholder="Your name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-600" placeholder="you@company.com" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-600" placeholder="(555) 123-4567" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                  <input type="text" value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-600" placeholder="Your company" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tell us about your project</label>
                <textarea rows={4} value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-600" placeholder="What are you looking to build or improve?" />
              </div>
              <button type="submit" disabled={status === 'sending'}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-lg font-medium text-lg transition disabled:opacity-50">
                {status === 'sending' ? 'Sending...' : 'Request My Free Growth Audit'}
              </button>
              {status === 'error' && (
                <p className="text-red-600 text-center">Something went wrong. Please try again.</p>
              )}
            </form>
            <button onClick={() => setStep(s => s - 1)} className="mt-6 text-gray-500 hover:text-gray-700 text-sm">
              &larr; Back
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
